import React from 'react';

import { Box, Typography } from '@material-ui/core';

import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import PeopleAltOutlinedIcon from '@material-ui/icons/PeopleAltOutlined';

import { EmptyCollection } from '../../../../shared_elements';
import { TEXT } from '../../shared_components';

const STAGES_PER_ROW = 4;

/* -------------------------------------------------------
 * COLORS
 * ----------------------------------------------------- */

const HEX_PATTERN = /^#([0-9a-f]{6})$/i;

/* Legacy preset ids from before the free-form colour picker, kept only to render
   stages saved under the old contract. */
const LEGACY_PRESET_HEX = {
  blue: '#1677FF',
  green: '#12B76A',
  orange: '#FF7900',
  purple: '#673DE6',
  red: '#F04438',
  grey: '#8B949E',
};

const resolveColourHex = (colourId) => (HEX_PATTERN.test(colourId) ? colourId : LEGACY_PRESET_HEX[colourId] || LEGACY_PRESET_HEX.blue);

/* Light tint of the colour, used as the node background behind the solid colourHex. */
const toLightHex = (hex) => `${hex}1A`;

/* -------------------------------------------------------
 * SMALL BADGE
 * ----------------------------------------------------- */

const StatusBadge = ({ label, color, background }) => (
  <Box style={{ position: 'absolute', top: 7, right: 8, padding: '2px 7px', borderRadius: 20, backgroundColor: background, color, fontSize: 9, fontWeight: 700, lineHeight: '14px' }}>
    {label}
  </Box>
);

/* -------------------------------------------------------
 * STAGE NODE
 * ----------------------------------------------------- */

const StageNode = ({ stage }) => {
  const stageTypeId = stage?.stage_type?.id || 'standard';

  const colourHex = resolveColourHex(stage?.colour);
  const backgroundHex = toLightHex(colourHex);

  const isStart = stageTypeId === 'start';
  const isFinal = stageTypeId === 'final';

  return (
    <Box style={{ width: '100%', minHeight: 82, boxSizing: 'border-box', border: `2px solid ${colourHex}`, borderRadius: 10, backgroundColor: backgroundHex, padding: '12px 12px 10px', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: `0 2px 8px ${colourHex}18`, transition: 'all 0.2s ease' }}>
      {isStart && (
        <StatusBadge label="START" color="#1677FF" background="#1677FF18" />
      )}

      {isFinal && (
        <StatusBadge label="FINAL" color="#16A34A" background="#16A34A18" />
      )}

      {/* Stage title */}
      <Box style={{ display: 'flex', alignItems: 'center', minWidth: 0, paddingRight: isStart || isFinal ? 35 : 0 }}>
        <Box style={{ width: 25, height: 25, minWidth: 25, borderRadius: '50%', backgroundColor: colourHex, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, marginRight: 8 }}>
          {stage?.order}
        </Box>

        <Typography noWrap style={{ fontSize: 13, fontWeight: 700, color: TEXT.primary, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {stage?.name || 'Unnamed Stage'}
        </Typography>
      </Box>

      {/* Project count */}
      <Box style={{ display: 'flex', alignItems: 'center', marginTop: 7, marginLeft: 33 }}>
        <PeopleAltOutlinedIcon style={{ fontSize: 13, color: TEXT.tertiary, marginRight: 4 }} />

        <Typography style={{ fontSize: 10, fontWeight: 600, color: TEXT.tertiary }}>
          {stage?.linked_plan_count || 0} projects
        </Typography>
      </Box>
    </Box>
  );
};

/* -------------------------------------------------------
 * GUARD / ACTION BADGE
 * ----------------------------------------------------- */

const RuleBadge = ({ guardCount, actionCount }) => {
  if (guardCount === 0 && actionCount === 0) {
    return null;
  }

  return (
    <Box style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 8px', borderRadius: 20, backgroundColor: '#F1F5F9', color: '#475569', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', marginTop: 4 }}>
      <span style={{ color: '#F59E0B', marginRight: 3 }}>{guardCount}G</span>

      <span style={{ color: '#8B5CF6' }}>{actionCount}A</span>
    </Box>
  );
};

/* -------------------------------------------------------
 * HORIZONTAL TRANSITION
 * ----------------------------------------------------- */

const Transition = ({ transition, direction = 'forward' }) => {
  const guardCount = (transition?.guards || []).length;
  const actionCount = (transition?.actions || []).length;

  const isBackward = direction === 'backward';

  return (
    <Box style={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 5px', boxSizing: 'border-box' }}>
      {/* Transition label */}
      {transition?.label && (
        <Typography noWrap title={transition.label} style={{ maxWidth: '100%', fontSize: 9, fontWeight: 600, color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center', marginBottom: 2 }}>
          {transition.label}
        </Typography>
      )}

      {/* Arrow */}
      <Box style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
        {isBackward && (
          <ArrowBackIcon style={{ fontSize: 20, color: '#94A3B8' }} />
        )}

        <Box style={{ flex: 1, height: 2, backgroundColor: '#CBD5E1' }} />

        {!isBackward && (
          <ArrowForwardIcon style={{ fontSize: 20, color: '#94A3B8' }} />
        )}
      </Box>

      <RuleBadge guardCount={guardCount} actionCount={actionCount} />
    </Box>
  );
};

/* -------------------------------------------------------
 * VERTICAL ROW CONNECTOR
 * ----------------------------------------------------- */

const RowConnector = ({ transition, direction }) => {
  const guardCount = (transition?.guards || []).length;
  const actionCount = (transition?.actions || []).length;

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 0' }}>
      {transition?.label && (
        <Typography noWrap style={{ maxWidth: 120, fontSize: 9, fontWeight: 600, color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center', marginBottom: 2 }}>
          {transition.label}
        </Typography>
      )}

      <ArrowDownwardIcon style={{ fontSize: 20, color: '#94A3B8' }} />

      <RuleBadge guardCount={guardCount} actionCount={actionCount} />
    </Box>
  );
};

/* -------------------------------------------------------
 * EXCEPTION NODE
 * ----------------------------------------------------- */

const ExceptionNode = ({ transition }) => {
  if (!transition) {
    return null;
  }

  const guardCount = (transition?.guards || []).length;
  const actionCount = (transition?.actions || []).length;

  return (
    <Box style={{ marginTop: 24, paddingTop: 18, borderTop: '1px dashed #CBD5E1', display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
      {/* Override label */}
      <Typography style={{ fontSize: 12, fontWeight: 700, color: '#EA580C', marginRight: 12 }}>
        Admin / Lead override
      </Typography>

      <ArrowForwardIcon style={{ color: '#EF4444', fontSize: 22, marginRight: 12 }} />

      {/* Exception */}
      <Box style={{ minWidth: 220, minHeight: 62, border: '2px solid #EF4444', borderRadius: 10, backgroundColor: '#FFF1F2', display: 'flex', alignItems: 'center', padding: '9px 13px', boxSizing: 'border-box' }}>
        <Box style={{ width: 32, height: 32, minWidth: 32, borderRadius: '50%', backgroundColor: '#EF4444', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10 }}>
          <ErrorOutlineIcon style={{ fontSize: 19 }} />
        </Box>

        <Box>
          <Typography style={{ fontSize: 13, fontWeight: 700, color: '#DC2626' }}>
            {transition?.to_stage?.name || 'Exception / Cancelled'}
          </Typography>

          <Typography style={{ fontSize: 10, color: '#64748B', marginTop: 2 }}>
            {guardCount} Guard{guardCount === 1 ? '' : 's'} · {actionCount} Action{actionCount === 1 ? '' : 's'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/* -------------------------------------------------------
 * LEGEND
 * ----------------------------------------------------- */

const LegendItem = ({ label, color, background }) => (
  <Box style={{ display: 'flex', alignItems: 'center', padding: '4px 8px', borderRadius: 20, backgroundColor: background, color, fontSize: 10, fontWeight: 600 }}>
    <Box style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color, marginRight: 5 }} />

    {label}
  </Box>
);

const Legend = () => (
  <Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 7, marginTop: 20 }}>
    <LegendItem label="Start" color="#1677FF" background="#1677FF12" />

    <LegendItem label="Final" color="#16A34A" background="#16A34A12" />

    <LegendItem label="Guard" color="#F59E0B" background="#F59E0B12" />

    <LegendItem label="Action" color="#8B5CF6" background="#8B5CF612" />

    <Box style={{ display: 'flex', alignItems: 'center', marginLeft: 5, color: '#64748B', fontSize: 10 }}>
      <ArrowForwardIcon style={{ fontSize: 16, marginRight: 3 }} />

      Transition
    </Box>
  </Box>
);

/* -------------------------------------------------------
 * MAIN DIAGRAM
 * ----------------------------------------------------- */

const DiagramTab = ({ stages, transitions }) => {
  const stageList = [...(stages || [])].sort((a, b) => (a?.order || 0) - (b?.order || 0));
  const transitionList = transitions || [];

  /* ---------------------------------------------
   * Find transition between two stages
   * ------------------------------------------- */

  const transitionFor = (fromStage, toStage) => {
    if (!fromStage || !toStage) {
      return null;
    }

    return transitionList.find((transition) => transition?.from_stage?.id === fromStage?.id && transition?.to_stage?.id === toStage?.id);
  };

  /* ---------------------------------------------
   * Exception transition
   * ------------------------------------------- */

  const exceptionTransition = transitionList.find((transition) => transition?.to_stage && !stageList.some((stage) => stage?.id === transition?.to_stage?.id));

  /* ---------------------------------------------
   * Empty state
   * ------------------------------------------- */

  if (!stageList.length) {
    return (
      <EmptyCollection title="No stages configured for this template yet" />
    );
  }

  /* ---------------------------------------------
   * Split stages into rows
   * ------------------------------------------- */

  const rows = [];

  for (let i = 0; i < stageList.length; i += STAGES_PER_ROW) {
    rows.push(stageList.slice(i, i + STAGES_PER_ROW));
  }

  return (
    <Box style={{ width: '100%', boxSizing: 'border-box', backgroundColor: '#FCFDFE', padding: '18px 8px 20px', overflow: 'hidden' }}>
      {rows.map((row, rowIndex) => {
        const isReversed = rowIndex % 2 === 1;
        const nextRow = rows[rowIndex + 1];

        /*
         * Build the row's contents as a flat,
         * ordered list of stage/transition items
         * in actual LEFT-TO-RIGHT visual order.
         *
         * Normal row:   [S1, T, S2, T, S3, T, S4]
         * Reversed row: [S4, T, S3, T, S2, T, S1]
         *               (visual order only —
         *               transitions still look up
         *               their real from/to stages)
         */

        const visualStages = isReversed ? [...row].reverse() : row;

        const rowItems = [];

        visualStages.forEach((stage, visualIndex) => {
          rowItems.push({ type: 'stage', key: `stage-${stage?.id}`, stage });

          const nextVisualStage = visualStages[visualIndex + 1];

          if (nextVisualStage) {
            const fromStage = isReversed ? nextVisualStage : stage;
            const toStage = isReversed ? stage : nextVisualStage;

            rowItems.push({
              type: 'transition',
              key: `transition-${fromStage?.id}-${toStage?.id}`,
              transition: transitionFor(fromStage, toStage),
              direction: isReversed ? 'backward' : 'forward',
            });
          }
        });

        const lastLogicalStage = row[row.length - 1];
        const firstLogicalStage = row[0];

        return (
          <React.Fragment key={`row-${rowIndex}`}>

            {/* ================================
                STAGE ROW
            ================================= */}

            <Box
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                /*
                 * A partial row (fewer than STAGES_PER_ROW
                 * stages) must NOT stretch its stages to fill
                 * the width — they need to stay the same size
                 * as a full row's stages, anchored to the side
                 * the row continues from, with the leftover
                 * space left empty.
                 */
                justifyContent: isReversed ? 'flex-end' : 'flex-start',
                gap: 8,
                boxSizing: 'border-box',
              }}
            >
              {rowItems.map((item) => {
                if (item.type === 'stage') {
                  return (
                    <Box key={item.key} style={{ flex: `0 0 calc((100% - ${(STAGES_PER_ROW - 1) * 70}px - ${(STAGES_PER_ROW * 2 - 2) * 8}px) / ${STAGES_PER_ROW})`, minWidth: 0 }}>
                      <StageNode stage={item.stage} />
                    </Box>
                  );
                }

                return (
                  <Box key={item.key} style={{ flex: '0 0 70px', minWidth: 0 }}>
                    <Transition transition={item.transition} direction={item.direction} />
                  </Box>
                );
              })}
            </Box>

            {/* ================================
                ROW CONNECTOR
            ================================= */}

            {nextRow && (
              <Box
                style={{
                  width: '100%',
                  display: 'flex',
                  height: 52,
                  boxSizing: 'border-box',
                  /*
                   * Normal row ends on the right, so the
                   * connector down to the next row starts on
                   * the right. Reversed row ends on the left
                   * (visually), so the connector starts on
                   * the left.
                   */
                  justifyContent: isReversed ? 'flex-start' : 'flex-end',
                }}
              >
                <Box style={{ width: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <RowConnector
                    transition={isReversed ? transitionFor(firstLogicalStage, nextRow[0]) : transitionFor(lastLogicalStage, nextRow[0])}
                  />
                </Box>
              </Box>
            )}
          </React.Fragment>
        );
      })}

      {/* -----------------------------------------
       * EXCEPTION
       * --------------------------------------- */}

      <ExceptionNode transition={exceptionTransition} />

      {/* -----------------------------------------
       * LEGEND
       * --------------------------------------- */}

      <Legend />

      {/* -----------------------------------------
       * SUMMARY
       * --------------------------------------- */}

      <Typography style={{ fontSize: 10, color: TEXT.tertiary, marginTop: 14, textAlign: 'right' }}>
        {stageList.length} stages · {Math.max(stageList.length - 1, 0)} transitions
      </Typography>
    </Box>
  );
};

export default DiagramTab;
